/* Live DIS publication status (FR-INTEROP-*).
 *
 * Interoperability is the loudest claim ORBIS makes against comparable products, and until this
 * panel existed there was nothing to point at: the Bridge was fully built and tested while the
 * running system emitted nothing, and nobody noticed for a week.
 *
 * Two things it shows that a PDU counter alone does not, both because a count on its own is
 * misleading:
 *
 *   * The TRANSPORT. "Sent 714 PDUs" from a capture transport means the bytes were encoded and
 *     went nowhere. An operator reading a number has no way to tell the difference.
 *   * The UNMAPPED entities, by name. A platform whose class carries no DIS enumeration is not
 *     transmitted at all — a placeholder type would make the receiving federate render and score
 *     the wrong platform. "Three entities not transmitted" sends someone hunting; naming them
 *     ends the search.
 *
 * Dead-reckoning suppression is shown too, because it looks like a fault otherwise. A federate
 * transmitting far fewer PDUs than it has entities is dead reckoning working correctly, and an
 * operator who cannot see that will read a low count as a broken bridge.
 */
import { useEffect, useState } from 'react'
import { Radio, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { baseUrl, authHeaders } from '../lib/gateway'

type Dis = {
  enabled: boolean
  reason?: string
  group?: string
  port?: number
  ttl?: number
  transport?: string
  pdusSent?: number
  bytesSent?: number
  sendFailures?: number
  lastError?: string
  suppressedByDeadReckoning?: number
  reasons?: Record<string, number>
  unmapped?: { entityId: string; designation: string; reason: string }[]
}

export function InteropPanel() {
  const open = useDeck((s) => s.interopOpen)
  const toggle = useDeck((s) => s.toggleInterop)
  const [dis, setDis] = useState<Dis | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    // Polled rather than pushed. This is diagnostics, not the picture: a second of staleness is
    // irrelevant, and adding it to the snapshot would grow every frame of the hot path for
    // something almost nobody has open.
    if (!open) return
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`${baseUrl()}/v1/interop/dis`, { headers: authHeaders() })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        if (!cancelled) { setDis(data); setError('') }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }
    void load()
    const t = window.setInterval(load, 1000)
    return () => { cancelled = true; window.clearInterval(t) }
  }, [open])

  if (!open) return null

  const n = (v: number | undefined) => (v ?? 0).toLocaleString()
  const kb = ((dis?.bytesSent ?? 0) / 1024).toFixed(1)

  return (
    <div className="panel absolute left-3 top-3 z-20 flex w-[26rem] flex-col overflow-hidden">
      <header className="flex items-center gap-2 border-b px-3 py-2"
              style={{ borderColor: 'var(--color-line-2)' }}>
        <Radio size={14} style={{ color: 'var(--color-accent)' }} />
        <span className="text-xs font-semibold tracking-wide">INTEROPERABILITY — DIS v7</span>
        <button className="ctl ml-auto" onClick={toggle} aria-label="Close interoperability panel">
          <X size={13} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3 text-xs">
        {error && <p style={{ color: 'var(--color-danger)' }}>Could not read DIS status: {error}</p>}

        {dis && !dis.enabled && (
          <p style={{ color: 'var(--color-text-3)' }}>
            DIS publication is <b>off</b>{dis.reason ? ` (${dis.reason})` : ''}. The simulation runs
            normally; nothing is transmitted.
          </p>
        )}

        {dis?.enabled && (
          <>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              <dt style={{ color: 'var(--color-text-3)' }}>Destination</dt>
              <dd className="num">{dis.group}:{dis.port}</dd>

              <dt style={{ color: 'var(--color-text-3)' }}>TTL</dt>
              {/* Named, because TTL 1 confines traffic to the local subnet and is the single
                  most common cause of "the other federate cannot see us" — and it fails with no
                  error at all, so nothing else on this panel would hint at it. */}
              <dd className="num">
                {dis.ttl}
                {dis.ttl === 1 && (
                  <span style={{ color: 'var(--color-warn)' }}> — local subnet only</span>
                )}
              </dd>

              <dt style={{ color: 'var(--color-text-3)' }}>Transport</dt>
              <dd className="num">{dis.transport}</dd>

              <dt style={{ color: 'var(--color-text-3)' }}>PDUs sent</dt>
              <dd className="num">{n(dis.pdusSent)} <span style={{ color: 'var(--color-text-3)' }}>({kb} kB)</span></dd>

              <dt style={{ color: 'var(--color-text-3)' }}>Suppressed</dt>
              <dd className="num">
                {n(dis.suppressedByDeadReckoning)}
                <span style={{ color: 'var(--color-text-3)' }}> by dead reckoning</span>
              </dd>

              <dt style={{ color: 'var(--color-text-3)' }}>Failures</dt>
              <dd className="num" style={{ color: dis.sendFailures ? 'var(--color-danger)' : undefined }}>
                {n(dis.sendFailures)}
              </dd>
            </dl>

            {dis.lastError && (
              <p className="mt-2" style={{ color: 'var(--color-danger)' }}>{dis.lastError}</p>
            )}

            {dis.reasons && Object.keys(dis.reasons).length > 0 && (
              <>
                <p className="mt-3 mb-1 tracking-wide" style={{ color: 'var(--color-text-3)' }}>
                  WHY EACH PDU WENT OUT
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(dis.reasons).map(([k, v]) => (
                    <span key={k} className="num rounded px-1.5 py-0.5"
                          style={{ background: 'var(--color-surface-0)' }}>
                      {k} {v.toLocaleString()}
                    </span>
                  ))}
                </div>
                <p className="mt-1.5" style={{ color: 'var(--color-text-3)' }}>
                  Mostly <span className="num">heartbeat</span> means the picture is stable; mostly
                  <span className="num"> position</span> means units are manoeuvring.
                </p>
              </>
            )}

            <p className="mt-3 mb-1 tracking-wide" style={{ color: 'var(--color-text-3)' }}>
              NOT TRANSMITTED
            </p>
            {dis.unmapped && dis.unmapped.length > 0 ? (
              <ul className="space-y-1">
                {dis.unmapped.map((u) => (
                  <li key={u.entityId}>
                    <b>{u.designation}</b>{' '}
                    <span style={{ color: 'var(--color-text-3)' }}>{u.reason}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: 'var(--color-text-3)' }}>
                Every platform carries a DIS enumeration. Nothing is being withheld.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
